/**
 * Side-anchored drawer built on the same Radix Dialog primitive as
 * components/ui/dialog.tsx — same overlay, same focus trap and Escape
 * handling, different geometry. Styled in the calendar's vocabulary
 * (white card, #e8e4df borders, #d7ad6c accent).
 */

import * as SheetPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '@/utils/misc.tsx'

const Sheet = SheetPrimitive.Root

const SheetTrigger = SheetPrimitive.Trigger

const SheetPortal = SheetPrimitive.Portal

const SheetClose = SheetPrimitive.Close

const SheetOverlay = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Overlay>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Overlay>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Overlay
		ref={ref}
		className={cn(
			'fixed inset-0 z-50 bg-[#3d3428]/40 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
			className,
		)}
		{...props}
	/>
))
SheetOverlay.displayName = SheetPrimitive.Overlay.displayName

const SheetContent = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Content>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Content>
>(({ className, children, ...props }, ref) => (
	<SheetPortal>
		<SheetOverlay />
		<SheetPrimitive.Content
			ref={ref}
			className={cn(
				'fixed inset-y-0 right-0 z-50 flex h-full w-full flex-col border-l border-[#e8e4df] bg-white shadow-[0_25px_50px_-12px_rgba(61,52,40,0.25)] transition duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right sm:max-w-md',
				className,
			)}
			{...props}
		>
			{children}
			<SheetPrimitive.Close className="absolute right-4 top-4 rounded-full p-2 text-[#8c8275] transition-all hover:bg-[#f5ebe0] hover:text-[#3d3428] focus:outline-none focus:ring-2 focus:ring-[#d7ad6c] focus:ring-offset-2 disabled:pointer-events-none">
				<X className="h-5 w-5" />
				<span className="sr-only">Zavřít</span>
			</SheetPrimitive.Close>
		</SheetPrimitive.Content>
	</SheetPortal>
))
SheetContent.displayName = SheetPrimitive.Content.displayName

const SheetHeader = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn(
			'shrink-0 border-b border-[#e8e4df] px-5 py-4 pr-14',
			className,
		)}
		{...props}
	/>
)
SheetHeader.displayName = 'SheetHeader'

/** Scroll region — the drawer body must scroll, not the page behind it. */
const SheetBody = ({
	className,
	...props
}: React.HTMLAttributes<HTMLDivElement>) => (
	<div
		className={cn('flex-1 overflow-y-auto px-5 py-4', className)}
		{...props}
	/>
)
SheetBody.displayName = 'SheetBody'

const SheetTitle = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Title>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Title>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Title
		ref={ref}
		className={cn(
			'text-lg font-semibold leading-none tracking-tight text-[#3d3428]',
			className,
		)}
		{...props}
	/>
))
SheetTitle.displayName = SheetPrimitive.Title.displayName

const SheetDescription = React.forwardRef<
	React.ElementRef<typeof SheetPrimitive.Description>,
	React.ComponentPropsWithoutRef<typeof SheetPrimitive.Description>
>(({ className, ...props }, ref) => (
	<SheetPrimitive.Description
		ref={ref}
		className={cn('mt-1 text-sm text-[#6b5c4c]', className)}
		{...props}
	/>
))
SheetDescription.displayName = SheetPrimitive.Description.displayName

export {
	Sheet,
	SheetPortal,
	SheetOverlay,
	SheetClose,
	SheetTrigger,
	SheetContent,
	SheetHeader,
	SheetBody,
	SheetTitle,
	SheetDescription,
}
